import {
  ApartmentOutlined,
  ArrowRightOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { ApiError, apiClient } from "@nimbus-erp/api-client";
import { BrandMark } from "@nimbus-erp/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Form, Input } from "antd";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { canNavigate, firstBusinessRoute } from "../utils/navigationAccess";

import { announceErpSessionChange } from "../utils/sessionChange";
import { readLoginIdentity, saveLoginIdentity } from "../utils/loginPreference";

interface LoginFormValues {
  tenantCode: string;
  username: string;
  password: string;
}

export function ErpLoginPage() {
  const [form] = Form.useForm<LoginFormValues>();
  const [savedIdentity] = useState(() => readLoginIdentity(localStorage));
  const [rememberIdentity, setRememberIdentity] = useState(!!savedIdentity);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { user, setInitialized, setUser } = useAuthStore();
  const loginInFlight = useRef(false);
  const from =
    (location.state as { from?: string } | null)?.from ?? "/workspace";

  useEffect(() => {
    if (user?.loginType === "ERP")
      navigate(
        canNavigate(from, user.permissions)
          ? from
          : firstBusinessRoute(user.permissions),
        { replace: true },
      );
  }, [from, navigate, user]);

  const loginMutation = useMutation({
    mutationFn: (values: LoginFormValues) =>
      apiClient.login({
        loginType: "ERP",
        tenantCode: values.tenantCode.trim(),
        username: values.username.trim(),
        password: values.password,
      }),
    onSuccess: (session) => {
      saveLoginIdentity(localStorage, rememberIdentity, form.getFieldsValue());
      announceErpSessionChange();
      queryClient.clear();
      queryClient.setQueryData(["session"], session);
      setUser(session);
      setInitialized(true);
      navigate(
        canNavigate(from, session.permissions)
          ? from
          : firstBusinessRoute(session.permissions),
        { replace: true },
      );
    },
    onSettled: () => {
      loginInFlight.current = false;
    },
  });

  const submitLogin = (values: LoginFormValues) => {
    if (loginInFlight.current || loginMutation.isPending) return;
    loginInFlight.current = true;
    loginMutation.mutate(values);
  };

  return (
    <main className="login-modern">
      <div className="login-modern__window">
        <section className="login-brand-panel" aria-label="云猫 ERP 品牌介绍">
          <header className="login-brand-panel__header">
            <BrandMark name="云猫 ERP" subtitle="BUSINESS OPERATIONS SUITE" />
            <span>企业业务管理平台</span>
          </header>

          <div className="login-brand-panel__content">
            <span className="login-brand-panel__eyebrow">企业经营管理系统</span>
            <h1>让企业经营清晰、有序、可追溯</h1>
            <p>贯通采购、销售、库存与财务，让业务数据形成可追溯的完整链条。</p>

            <div className="login-brand-panel__capabilities">
              <span>进销存与业财协同</span>
              <span>多组织协同</span>
              <span>全链路追溯</span>
            </div>

            <div className="login-factory-blueprint" aria-hidden="true">
              <svg viewBox="0 0 760 330" role="presentation">
                <g className="blueprint-grid">
                  <path d="M0 274H760M0 226H760M0 178H760M0 130H760" />
                  <path d="M86 86V330M172 86V330M258 86V330M344 86V330M430 86V330M516 86V330M602 86V330M688 86V330" />
                </g>
                <g className="blueprint-building">
                  <rect x="35" y="92" width="196" height="150" rx="12" />
                  <rect x="283" y="58" width="196" height="184" rx="12" />
                  <rect x="531" y="92" width="196" height="150" rx="12" />
                  <path d="M35 131H231M283 97H479M531 131H727" />
                </g>
                <g className="blueprint-line">
                  <path d="M231 168H283M479 168H531" />
                  <path d="M268 158L283 168L268 178M516 158L531 168L516 178" />
                  <path d="M58 157H148M58 179H201M58 201H177" />
                  <path d="M306 127H413M306 149H449M306 171H428M306 193H457" />
                  <path d="M554 157H677M554 179H700M554 201H655" />
                </g>
                <g className="blueprint-crane">
                  <circle cx="62" cy="112" r="6" />
                  <circle cx="310" cy="78" r="6" />
                  <circle cx="558" cy="112" r="6" />
                </g>
                <g className="blueprint-boxes">
                  <path d="M58 220H208M306 220H456M554 220H704" />
                </g>
              </svg>
              <span>业务协同 · 数据贯通</span>
            </div>
          </div>

          <footer className="login-brand-panel__footer">
            <SafetyCertificateOutlined />
            企业级数据隔离与操作审计
          </footer>
        </section>

        <section className="login-auth-panel">
          <header className="login-auth-panel__header">
            <span className="login-modern__service">
              <i /> 企业账套安全登录
            </span>
          </header>

          <section className="login-sheet" aria-label="企业账套登录">
            <div className="login-sheet__heading">
              <small>企业账套登录</small>
              <h1>登录云猫 ERP</h1>
              <p>请输入企业与账号信息</p>
            </div>

            {loginMutation.isError && (
              <Alert
                type="error"
                showIcon
                message={
                  loginMutation.error instanceof ApiError
                    ? loginMutation.error.message
                    : "登录失败，请稍后重试"
                }
                description={
                  loginMutation.error instanceof ApiError &&
                  loginMutation.error.traceId ? (
                    <span className="login-sheet__trace">
                      <span>追踪编号</span>
                      <code>{loginMutation.error.traceId}</code>
                    </span>
                  ) : undefined
                }
                closable
                onClose={() => loginMutation.reset()}
              />
            )}

            <Form<LoginFormValues>
              form={form}
              layout="vertical"
              requiredMark={false}
              initialValues={savedIdentity ?? {}}
              onFinish={submitLogin}
              size="large"
              disabled={loginMutation.isPending}
            >
              <Form.Item
                label="企业编码"
                name="tenantCode"
                rules={[{ required: true, message: "请输入企业编码" }]}
              >
                <Input
                  placeholder="例如：COMPANY_001"
                  prefix={<ApartmentOutlined />}
                  autoComplete="organization"
                />
              </Form.Item>
              <Form.Item
                label="登录账号"
                name="username"
                rules={[{ required: true, message: "请输入登录账号" }]}
              >
                <Input
                  placeholder="企业管理员分配的登录账号"
                  prefix={<UserOutlined />}
                  autoComplete="username"
                />
              </Form.Item>
              <Form.Item
                label="登录密码"
                name="password"
                rules={[{ required: true, message: "请输入登录密码" }]}
              >
                <Input.Password
                  placeholder="请输入登录密码"
                  prefix={<LockOutlined />}
                  autoComplete="current-password"
                />
              </Form.Item>
              <div className="login-sheet__options">
                <Checkbox
                  checked={rememberIdentity}
                  onChange={(event) => {
                    setRememberIdentity(event.target.checked);
                    if (!event.target.checked)
                      saveLoginIdentity(
                        localStorage,
                        false,
                        form.getFieldsValue(),
                      );
                  }}
                >
                  记住企业和账号
                </Checkbox>
                <span>忘记密码请联系企业管理员</span>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loginMutation.isPending}
                disabled={loginMutation.isPending}
                className="login-sheet__submit"
              >
                {loginMutation.isPending ? "正在验证账套" : "进入系统"}
                {!loginMutation.isPending && <ArrowRightOutlined />}
              </Button>
            </Form>

            <footer className="login-sheet__security">
              <LockOutlined /> 登录行为将写入企业安全审计日志
            </footer>
          </section>

          <footer className="login-modern__footer">
            <span>云猫 ERP · 企业业务管理平台</span>
            <span>{new Date().toLocaleDateString("zh-CN")}</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
            >
              蜀ICP备2026022567号-2
            </a>
          </footer>
        </section>
      </div>
    </main>
  );
}
